import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  HeartHandshake, Search, Phone, AlertTriangle, Heart, 
  Brain, Flame, Droplet, Loader2, ChevronRight, ChevronDown,
  Zap, Skull, Waves, Thermometer, AlertCircle, X
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const FirstAidGuide = ({ user, setUser }) => {
  const [categories, setCategories] = useState({});
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    loadGuides();
  }, []);

  const loadGuides = async () => {
    try {
      const response = await axios.get(`${API}/first-aid/guides`);
      setCategories(response.data.categories || {});
      // Expand first category by default
      const firstCategory = Object.keys(response.data.categories || {})[0];
      if (firstCategory) {
        setExpandedCategories({ [firstCategory]: true });
      }
    } catch (error) {
      console.error('Error loading guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGuideDetails = async (guideId) => {
    try {
      const response = await axios.get(`${API}/first-aid/guide/${guideId}`);
      setSelectedGuide(response.data.guide);
    } catch (error) {
      console.error('Error loading guide:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/first-aid/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getIcon = (iconName) => {
    const icons = {
      heart: Heart,
      'heart-pulse': Heart,
      'alert-triangle': AlertTriangle,
      droplet: Droplet,
      flame: Flame,
      brain: Brain,
      bone: AlertCircle,
      'alert-circle': AlertCircle,
      zap: Zap,
      skull: Skull,
      waves: Waves,
      thermometer: Thermometer
    };
    const Icon = icons[iconName] || AlertTriangle;
    return <Icon className="w-5 h-5" />;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[severity] || 'bg-gray-100 text-gray-700';
  };

  const getCategoryColor = (categoryId) => {
    const colors = {
      life_threatening: 'from-red-500 to-rose-500',
      injuries: 'from-orange-500 to-amber-500',
      neurological: 'from-purple-500 to-violet-500',
      environmental: 'from-yellow-500 to-orange-500'
    };
    return colors[categoryId] || 'from-gray-500 to-slate-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-8 pt-24 flex justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <HeartHandshake className="w-8 h-8 text-teal-600" />
            First Aid Guide
          </h1>
          <p className="text-gray-600 mt-1">Step-by-step emergency medical guidance</p>
        </div>

        {/* Emergency Banner */}
        <Card className="mb-6 bg-gradient-to-r from-red-500 to-rose-500 text-white border-0">
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6" />
                <span className="font-bold text-lg">In any emergency, call 112 first!</span>
              </div>
              <Button
                className="bg-white text-red-600 hover:bg-red-50"
                onClick={() => window.location.href = 'tel:112'}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call 112
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="flex gap-2 mb-6">
          <Input
            placeholder="Search first aid guides..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (!e.target.value.trim()) setSearchResults([]);
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1"
          />
          <Button onClick={handleSearch}>
            <Search className="w-4 h-4" />
          </Button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Search Results ({searchResults.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    className="w-full p-3 rounded-lg border hover:border-teal-300 text-left flex items-center justify-between"
                    onClick={() => loadGuideDetails(result.id)}
                  >
                    <div>
                      <p className="font-medium">{result.title}</p>
                      <p className="text-sm text-gray-500">{result.when_to_use}</p>
                    </div>
                    <Badge className={getSeverityColor(result.severity)}>{result.severity}</Badge>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Selected Guide Detail */}
        {selectedGuide && (
          <Card className="mb-6 border-2 border-teal-200">
            <CardHeader className="bg-gradient-to-r from-teal-50 to-green-50">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    {getIcon(selectedGuide.icon)}
                    {selectedGuide.title}
                  </CardTitle>
                  <CardDescription className="mt-2">{selectedGuide.when_to_use}</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedGuide(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2 mt-3">
                <Badge className={getSeverityColor(selectedGuide.severity)}>
                  {selectedGuide.severity.toUpperCase()}
                </Badge>
                {selectedGuide.call_emergency && (
                  <Badge className="bg-red-100 text-red-700">
                    <Phone className="w-3 h-3 mr-1" />
                    Call 112
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Steps */}
              <div className="space-y-4 mb-6">
                {selectedGuide.steps.map((step, index) => (
                  <div key={`item-${index}`} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <div className="flex-1 pt-1">
                      <h4 className="font-semibold text-gray-800">{step.title}</h4>
                      <p className="text-gray-600 mt-1">{step.instruction}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tips */}
              {selectedGuide.tips && selectedGuide.tips.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-blue-800 mb-2">💡 Tips</h4>
                  <ul className="list-disc list-inside text-blue-700 text-sm space-y-1">
                    {selectedGuide.tips.map((tip, i) => (
                      <li key={`item-${i}`}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Warning Signs */}
              {selectedGuide.warning_signs && selectedGuide.warning_signs.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Warning Signs
                  </h4>
                  <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                    {selectedGuide.warning_signs.map((sign, i) => (
                      <li key={`item-${i}`}>{sign}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Call 112 If */}
              {selectedGuide.call_112_if && selectedGuide.call_112_if.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Call 112 If:
                  </h4>
                  <ul className="list-disc list-inside text-orange-700 text-sm space-y-1">
                    {selectedGuide.call_112_if.map((condition, i) => (
                      <li key={`item-${i}`}>{condition}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Categories */}
        <div className="space-y-4">
          {Object.entries(categories).map(([categoryId, category]) => (
            <Card key={categoryId} className="overflow-hidden">
              <button
                className={`w-full p-4 text-left bg-gradient-to-r ${getCategoryColor(categoryId)} text-white flex items-center justify-between`}
                onClick={() => toggleCategory(categoryId)}
              >
                <div>
                  <h3 className="font-bold text-lg">{category.name}</h3>
                  <p className="text-white/80 text-sm">{category.guides.length} guides</p>
                </div>
                {expandedCategories[categoryId] ? (
                  <ChevronDown className="w-6 h-6" />
                ) : (
                  <ChevronRight className="w-6 h-6" />
                )}
              </button>
              
              {expandedCategories[categoryId] && (
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {category.guides.map((guide) => (
                      <button
                        key={guide.id}
                        className="w-full p-4 rounded-lg border hover:border-teal-300 hover:bg-teal-50 text-left flex items-center justify-between transition-colors"
                        onClick={() => loadGuideDetails(guide.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${getSeverityColor(guide.severity)}`}>
                            {getIcon(guide.icon)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{guide.title}</p>
                            <p className="text-sm text-gray-500 line-clamp-1">{guide.when_to_use}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {guide.call_emergency && (
                            <Badge className="bg-red-100 text-red-700">
                              <Phone className="w-3 h-3" />
                            </Badge>
                          )}
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FirstAidGuide;
